"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";

import { JobProgress } from "@/components/jobs/job-progress";
import { useShell } from "@/components/shell/shell-context";
import { ApiError, api, apiDownload, loginUrl } from "@/lib/api/client";
import type { LinkingOverview, LinkingStarted, LinkingSuggestion, Project } from "@/lib/api/types";
import { saveBlob } from "@/lib/download";
import { formatCount, formatDateTime } from "@/lib/format";
import { DEFAULT_FILTERS, type Filters, RULES, type StatusFilter, filterSuggestions, formatScore, shortUrl, targetLine } from "@/lib/links";

import { SuggestionCard } from "./suggestion-card";
import { TargetPanel } from "./target-panel";

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: "offen", label: "Offen" },
  { key: "uebernommen", label: "Übernommen" },
  { key: "verworfen", label: "Verworfen" },
  { key: "alle", label: "Alle" },
];
const SIMILARITY_CHOICES = [0, 0.3, 0.4, 0.5, 0.6];
const FIELD = "h-11 rounded-md border border-field bg-white px-3 text-sm";

export function LinksView() {
  const { project } = useShell();
  // Bei Projektwechsel neu aufbauen: Vorschläge, Filter und Rückfragen gehören zum Projekt
  return project ? <View key={project.id} project={project} /> : null;
}

function message(caught: unknown): string {
  return caught instanceof Error ? caught.message : "Unbekannter Fehler";
}

function Kpi({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-line bg-white px-5 py-4">
      <span className="text-[13px] text-muted">{label}</span>
      <span className="text-[26px] font-semibold tracking-tight">{value}</span>
      {detail && <span className="text-xs text-muted">{detail}</span>}
    </div>
  );
}

function View({ project }: { project: Project }) {
  const router = useRouter();
  const [overview, setOverview] = useState<LinkingOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [similarity, setSimilarity] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<LinkingSuggestion[] | null>(null);

  const load = useCallback(
    () =>
      api<LinkingOverview>(`/api/linking/overview?project_id=${project.id}`)
        .then((next) => {
          setOverview(next);
          setSuggestions(next.suggestions);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (caught instanceof ApiError && caught.status === 401) {
            router.replace(loginUrl("/links"));
            return;
          }
          setError(message(caught));
        }),
    [project.id, router],
  );

  useEffect(() => {
    void load();
  }, [load, reload]);

  const items = useMemo(() => suggestions ?? [], [suggestions]);
  const shown = useMemo(() => filterSuggestions(items, filters), [items, filters]);
  const counts = useMemo(
    () => ({
      offen: items.filter((i) => i.status === "offen").length,
      uebernommen: items.filter((i) => i.status === "uebernommen").length,
      verworfen: items.filter((i) => i.status === "verworfen").length,
    }),
    [items],
  );
  const running = overview?.run?.state === "running";
  const settings = overview?.settings;
  const minSimilarity = similarity ?? settings?.min_similarity ?? 0.3;

  const start = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await api<LinkingStarted>("/api/linking/runs", { method: "POST", json: { project_id: project.id, min_similarity: minSimilarity } });
      setAsking(false);
      setReload((current) => current + 1);
    } catch (caught) {
      setActionError(message(caught));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (item: LinkingSuggestion, status: LinkingSuggestion["status"]) => {
    setBusy(true);
    setActionError(null);
    try {
      const next = await api<LinkingSuggestion>(`/api/linking/suggestions/${item.id}?project_id=${project.id}`, { method: "PATCH", json: { status } });
      setSuggestions((list) => (list ?? []).map((entry) => (entry.id === next.id ? { ...entry, status: next.status } : entry)));
    } catch (caught) {
      setActionError(message(caught));
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async (only: "alle" | "uebernommen") => {
    setActionError(null);
    try {
      const file = await apiDownload(`/api/linking/export.csv?project_id=${project.id}&only=${only}`, "interne-links.csv");
      saveBlob(file.blob, file.filename);
    } catch (caught) {
      setActionError(message(caught));
    }
  };

  const graph = overview?.graph;
  const crawl = overview?.crawl;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-7 sm:px-9">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-[13px] text-muted">
            Interne Verlinkung ·{" "}
            {crawl
              ? `Basis: Crawl vom ${formatDateTime(crawl.created_at)} (${formatCount(crawl.url_count)} URLs, ${formatCount(crawl.link_count)} Links)`
              : "noch kein Crawl importiert"}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Linkpotenziale</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={counts.offen + counts.uebernommen + counts.verworfen === 0}
            onClick={() => void exportCsv("alle")}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
          >
            CSV exportieren
          </button>
          <button
            type="button"
            disabled={counts.uebernommen === 0}
            onClick={() => void exportCsv("uebernommen")}
            className="h-11 rounded-md border border-field bg-white px-3.5 text-sm disabled:opacity-60"
          >
            Übernommene als CSV
          </button>
          <button
            type="button"
            aria-expanded={asking}
            disabled={overview === null || !overview.available || running}
            onClick={() => setAsking((current) => !current)}
            className="h-11 rounded-md bg-accent px-4 text-sm font-medium text-white disabled:opacity-60"
          >
            {overview?.run?.state === "done" ? "Neu berechnen" : "Analyse starten"}
          </button>
        </div>
      </header>

      {error && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
          <span>{error}</span>
          <button type="button" onClick={() => setReload((current) => current + 1)} className="h-11 rounded-md border border-danger bg-white px-3.5">
            Erneut versuchen
          </button>
        </div>
      )}
      {overview && !overview.available && (
        <p role="status" className="rounded-md bg-warning-bg px-4 py-3 text-sm text-warning">
          {overview.reason}{" "}
          <Link href="/audit" className="underline">
            Zum Site Audit
          </Link>
        </p>
      )}
      {actionError && (
        <p role="alert" className="text-sm text-danger">
          {actionError}
        </p>
      )}

      {asking && settings && (
        <div role="group" aria-label="Analyse starten bestätigen" className="flex flex-col gap-3 rounded-md border border-line bg-white px-4 py-3 text-sm">
          <p>
            <strong className="font-medium">Linkpotenziale berechnen?</strong> Die Analyse wertet den Linkgraphen des Crawls aus, sucht thematisch ähnliche Seiten
            ohne Link (Title, H1, Meta, URL) und liest für die besten Vorschläge den Fließtext von bis zu {formatCount(settings.max_sources)} Quellseiten der
            eigenen Domain, um Satz und Anker zu finden. Kosten: keine, nur Abrufe von {project.domain} (etwa eine Seite je halbe Sekunde). Höchstens{" "}
            {formatCount(settings.max_per_source)} neue Links je Quellseite und je Zielseite.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="links-similarity" className="text-[13px] font-medium">
                Mindest-Ähnlichkeit
              </label>
              <select
                id="links-similarity"
                value={minSimilarity}
                onChange={(event) => setSimilarity(Number(event.target.value))}
                className={FIELD}
              >
                {[...new Set([settings.min_similarity, ...SIMILARITY_CHOICES.filter((c) => c > 0)])]
                  .sort((a, b) => a - b)
                  .map((choice) => (
                    <option key={choice} value={choice}>
                      {formatScore(choice)}
                    </option>
                  ))}
              </select>
            </div>
            <button type="button" disabled={busy} onClick={() => void start()} className="h-11 rounded-md bg-accent px-3.5 text-[13px] font-medium text-white disabled:opacity-60">
              Analyse starten
            </button>
            <button type="button" onClick={() => setAsking(false)} className="h-11 rounded-md border border-field bg-white px-3.5 text-[13px]">
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {overview && <TargetPanel project={project} available={overview.available} blocked={running} defaultSimilarity={overview.settings.target_min_similarity} />}

      {running && overview?.run?.job_id && (
        <JobProgress
          key={overview.run.job_id}
          jobId={overview.run.job_id}
          title="Linkpotenziale werden berechnet"
          onFinished={() => setReload((current) => current + 1)}
        />
      )}
      {overview?.run?.state === "failed" && (
        <p role="alert" className="rounded-md bg-danger-bg px-4 py-3 text-sm text-danger">
          Die Analyse ist fehlgeschlagen{overview.run.error ? `: ${overview.run.error}` : "."}
        </p>
      )}

      {overview === null && !error && <p className="text-sm text-muted">Linkpotenziale werden geladen …</p>}

      {overview && overview.available && overview.summary === null && !running && (
        <p className="rounded-lg border border-line bg-white px-5 py-6 text-sm text-muted">
          Noch keine Analyse. „Analyse starten“ berechnet Vorschläge für interne Links auf Basis des importierten Crawls.
        </p>
      )}

      {overview && overview.summary !== null && (
        <>
          <section aria-label="Kennzahlen" className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
            <Kpi label="Offene Vorschläge" value={formatCount(counts.offen)} detail={`${formatCount(counts.uebernommen)} übernommen, ${formatCount(counts.verworfen)} verworfen`} />
            <Kpi label="Orphan Pages" value={formatCount(graph?.orphans ?? 0)} detail={`${formatCount(graph?.no_content_links ?? 0)} ohne Link im Fließtext`} />
            <Kpi
              label="Ø Klicktiefe"
              value={graph?.avg_depth == null ? "–" : graph.avg_depth.toLocaleString("de-DE", { maximumFractionDigits: 1 })}
              detail={`${formatCount(graph?.deep ?? 0)} Seiten tiefer als 4`}
            />
            <Kpi
              label="Sätze gefunden"
              value={`${formatCount(overview.summary.found)} von ${formatCount(overview.summary.suggestions)}`}
              detail={overview.summary.failed > 0 ? `${formatCount(overview.summary.failed)} Quellseiten nicht abrufbar` : undefined}
            />
          </section>

          <section aria-label="Filter" className="flex flex-wrap items-center gap-2.5">
            <div role="group" aria-label="Status" className="flex flex-wrap gap-1.5">
              {STATUS_FILTERS.map((entry) => (
                <button
                  key={entry.key}
                  type="button"
                  aria-pressed={filters.status === entry.key}
                  onClick={() => setFilters({ ...filters, status: entry.key })}
                  className={`h-11 rounded-full border px-3.5 text-[13px] ${filters.status === entry.key ? "border-ink bg-ink text-white" : "border-field bg-white"}`}
                >
                  {entry.label}
                  {entry.key !== "alle" ? ` · ${formatCount(counts[entry.key])}` : ""}
                </button>
              ))}
            </div>
            {(
              [
                ["orphan", "Ziel: Orphan Pages"],
                ["deep", "Ziel: Klicktiefe > 4"],
                ["withSentence", "Nur mit Satz"],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="flex h-11 items-center gap-2 rounded-full border border-field bg-white px-3.5 text-[13px]">
                <input
                  type="checkbox"
                  checked={filters[key]}
                  onChange={(event) => setFilters({ ...filters, [key]: event.target.checked })}
                  className="h-4 w-4"
                />
                {label}
              </label>
            ))}
            <span className="flex-1" />
            <label htmlFor="links-min" className="text-[13px] text-muted">
              Min. Ähnlichkeit
            </label>
            <select
              id="links-min"
              value={filters.minSimilarity}
              onChange={(event) => setFilters({ ...filters, minSimilarity: Number(event.target.value) })}
              className={FIELD}
            >
              {SIMILARITY_CHOICES.map((choice) => (
                <option key={choice} value={choice}>
                  {choice === 0 ? "keine" : formatScore(choice)}
                </option>
              ))}
            </select>
          </section>

          <div className="flex flex-wrap items-start gap-5">
            <section aria-labelledby="suggestions-title" className="min-w-0 flex-[3_1_560px] rounded-lg border border-line bg-white">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#e6e9e7] px-5 py-3">
                <h2 id="suggestions-title" className="text-base font-semibold">
                  Vorschläge
                </h2>
                <span className="text-[13px] text-muted">
                  {formatCount(shown.length)} {shown.length === 1 ? "Vorschlag" : "Vorschläge"}, sortiert nach Score
                </span>
              </div>
              {shown.length === 0 && (
                <p className="px-5 py-6 text-sm text-muted">
                  {items.length === 0 ? "Die Analyse hat keine Vorschläge gefunden." : "Keine Vorschläge zu diesen Filtern."}
                </p>
              )}
              {shown.map((item) => (
                <SuggestionCard key={item.id} item={item} busy={busy} onDecide={(status) => void decide(item, status)} />
              ))}
            </section>

            <aside className="flex min-w-0 flex-[2_1_320px] flex-col gap-5">
              <section aria-labelledby="targets-title" className="flex flex-col gap-2 rounded-lg border border-line bg-white px-5 py-4">
                <h2 id="targets-title" className="text-base font-semibold">
                  Zielseiten mit Potenzial
                </h2>
                <span className="text-xs text-muted">Orphans, wenige Links im Fließtext, große Klicktiefe</span>
                {overview.targets.length === 0 && <span className="text-sm text-muted">Keine Zielseiten.</span>}
                <ul className="flex flex-col">
                  {overview.targets.map((target) => (
                    <li key={target.url} className="flex flex-col gap-0.5 border-t border-neutral-bg py-2 text-[13px]">
                      <span className="break-all font-mono">{shortUrl(target.url)}</span>
                      <span className="text-xs text-muted">
                        {formatCount(target.suggestions)} {target.suggestions === 1 ? "Vorschlag" : "Vorschläge"}
                        {targetLine(target) ? ` · ${targetLine(target)}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
              <section aria-labelledby="rules-title" className="flex flex-col gap-2 rounded-lg border border-line bg-white px-5 py-4">
                <h2 id="rules-title" className="text-base font-semibold">
                  Regeln
                </h2>
                <ul className="flex flex-col text-[13px] leading-snug">
                  {RULES.map((rule) => (
                    <li key={rule} className="border-t border-neutral-bg py-1.5">
                      {rule}
                    </li>
                  ))}
                  <li className="border-t border-neutral-bg py-1.5 text-muted">
                    Nicht enthalten: Search-Console-Positionen (nicht verbunden) und der Export nach Contentful. Die Ähnlichkeit kommt aus Title, H1, Meta und
                    URL, nicht aus dem ganzen Seitentext.
                  </li>
                </ul>
              </section>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
